import { SidekickCustomer } from 'src/models/Sidekick/SidekickCustomer';
import SidekickModuleSpec from 'src/models/Sidekick/SidekickModuleSpec';
import { NextData } from 'src/models/Sidekick/SidekickThreads';

export function omitProperties<T>(obj: T, fields: string[]): T {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return (obj as unknown[]).map(item => omitProperties(item, fields)) as unknown as T;
  } else {
    const newObj = {} as T;
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key) && !fields.includes(key)) {
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          newObj[key] = omitProperties(obj[key], fields);
          continue;
        }
        newObj[key] = obj[key];
      }
    }
    return newObj as T;
  }
}

export const deleteProperty = (obj: unknown, path: string) => {
  if (!obj) {
    return;
  }

  const keys: string[] = path.split('.') || [];
  let current = obj as Record<string, unknown>;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i] as string;
    if (!(key in current)) {
      return;
    }
    current = current[key] as Record<string, unknown>;
  }
  const index = keys[keys.length - 1] || 0;

  delete current[index];
};

export const arrayToObject = <T>(arr: T[], keyFieldName: string): Record<string, T> =>
  arr.reduce(
    (curr, item: T) => ({
      ...curr,
      [(item as Record<string, unknown>)[keyFieldName] as string]: item,
    }),
    {}
  );

export const getModuleSpec = (module: NextData, moduleSpecsMap: Record<string, SidekickModuleSpec>) => {
  let spec = moduleSpecsMap[module.name];
  if (!spec && module.externalAction) {
    const extAction = module.externalAction;
    const specKey = Object.keys(moduleSpecsMap).find(moduleNameSpec => {
      if (!moduleSpecsMap[moduleNameSpec].externalAction) {
        return false;
      }

      const extActionSpec = moduleSpecsMap[moduleNameSpec].externalAction;

      return extAction?.integrationId === extActionSpec?.integrationId && extAction?.name === extActionSpec?.name;
    });

    if (specKey) {
      spec = moduleSpecsMap[specKey];
    }
  }
  return spec;
};

export function getThankfulBaseUrl(customer: SidekickCustomer) {
  const notProdClusters = ['master', 'staging'];
  const gladlyConfig = window['gladlyConfig'] || {};
  const env = gladlyConfig['environment'] as string | undefined;
  const hostPrefix = env && notProdClusters.includes(env) ? 'dashboard-staging' : `dashboard`;

  return `https://${hostPrefix}.thankful.ai/c/${customer.slug}`;
}

export const getFriendlyName = (s: string): string => {
  // Split on capitals, join the words back together, and capitalize
  // the first letter. Adapted from https://stackoverflow.com/a/7888303

  const splitName = s
    .split(/(?=[A-Z])/)
    .map(str => str.toLowerCase())
    .join(' ');
  const fName = splitName.charAt(0).toUpperCase() + splitName.slice(1);

  if (fName.endsWith('store')) {
    return fName.slice(0, fName.length - 5).trim();
  }

  return fName;
};

export const convertDtVal = (inputVal: string[]): string => {
  if (inputVal.length !== 3) {
    throw new Error('invalid datetime input');
  }
  const today = new Date();
  const offset = 24 * 60 * 60 * 1000;
  const dtNum = parseInt(inputVal[0]);
  const dtType = inputVal[1];
  const dtWhen = inputVal[2];
  let timeInMs: number;
  if (isNaN(dtNum)) {
    throw new Error('invalid input for number');
  }
  switch (dtType) {
    case 'Days':
      timeInMs = dtNum * offset;
      break;
    case 'Hours':
      timeInMs = dtNum * (60 * 60 * 1000);
      break;
    case 'Minutes':
      timeInMs = dtNum * (60 * 1000);
      break;
    case 'Seconds':
      timeInMs = dtNum * 1000;
      break;
    case 'Business days':
      timeInMs = dtNum * offset;
      break;
    case 'Weeks':
      timeInMs = dtNum * (7 * offset);
      break;
    case 'Months':
      timeInMs = dtNum * (30 * offset);
      break;
    case 'Years':
      timeInMs = dtNum * (365 * offset);
      break;
    default:
      throw new Error('invalid time period for input');
  }
  if (dtWhen === 'Ago') {
    if (dtType === 'Business days') {
      const date = new Date(today.getTime() - timeInMs);
      if (date.getDay() === 0) {
        return new Date(date.getTime() - 2 * offset).toISOString();
      }
      if (date.getDay() === 6) {
        return new Date(date.getTime() - offset).toISOString();
      }
    }
    return new Date(today.getTime() - timeInMs).toISOString();
  } else {
    if (dtType === 'Business days') {
      const date = new Date(today.getTime() + timeInMs);
      if (date.getDay() === 0) {
        return new Date(date.getTime() + offset).toISOString();
      }
      if (date.getDay() === 6) {
        return new Date(date.getTime() + 2 * offset).toISOString();
      }
    }
    return new Date(today.getTime() + timeInMs).toISOString();
  }
};
